import type React from 'react';
import { Textarea as TextareaBase } from '@/components/ui/textarea';
import {
	RadioGroup as RadioGroupBase,
	RadioGroupItem as RadioGroupItemBase,
} from '@/components/ui/radio-group';
import type { StatsBlockKey } from '@sportspredict/shared';
import type { CampaignConfigJson } from '@/store/api';
import type {
	TextareaProps,
	RadioGroupProps,
	RadioGroupItemProps,
	FormState,
} from '@/types/admin-campaign.types';
import { STATS_BLOCKS } from '@/constants/admin-campaign.constants';

export const Textarea = TextareaBase as unknown as React.ComponentType<TextareaProps>;
export const RadioGroup = RadioGroupBase as unknown as React.ComponentType<RadioGroupProps>;
export const RadioGroupItem =
	RadioGroupItemBase as unknown as React.ComponentType<RadioGroupItemProps>;

export function emptyForm(): FormState {
	return {
		name: '',
		subject: '',
		intro_text: '',
		lobbyIdsRaw: '',
		startDate: '',
		endDate: '',
		minTradesInScope: 1,
		statsBlocks: {
			tradeCount: true,
			pnl: true,
			winRate: true,
			bestTrade: true,
			lobbyRank: true,
			sportBreakdown: false,
		},
		amongTraders: true,
	};
}

export function parseLobbyIds(raw: string): string[] {
	return raw
		.split(/[\s,]+/)
		.map((s) => s.trim())
		.filter(Boolean);
}

export function buildConfig(form: FormState, parsedLobbyIds: string[]): CampaignConfigJson {
	return {
		scope: {
			lobbyIds: parsedLobbyIds,
			dateRange: {
				start: new Date(`${form.startDate}T00:00:00Z`).toISOString(),
				end: new Date(`${form.endDate}T00:00:00Z`).toISOString(),
			},
		},
		audience: { minTradesInScope: form.minTradesInScope },
		statsBlocks: Object.keys(form.statsBlocks).filter(
			(k) => form.statsBlocks[k]
		) as StatsBlockKey[],
		lobbyRankConfig: { amongTraders: form.amongTraders },
	};
}

export function configToFormState(c: {
	name: string;
	subject: string;
	intro_text: string | null;
	config: CampaignConfigJson;
}): FormState {
	const blockState: Record<string, boolean> = {};
	STATS_BLOCKS.forEach((b) => {
		blockState[b.key] = c.config.statsBlocks.includes(b.key);
	});
	return {
		name: c.name,
		subject: c.subject,
		intro_text: c.intro_text ?? '',
		lobbyIdsRaw: c.config.scope.lobbyIds.join('\n'),
		startDate: c.config.scope.dateRange.start.slice(0, 10),
		endDate: c.config.scope.dateRange.end.slice(0, 10),
		minTradesInScope: c.config.audience.minTradesInScope,
		statsBlocks: blockState,
		amongTraders: c.config.lobbyRankConfig?.amongTraders ?? true,
	};
}
